import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Camera, Mic, Video, Calendar, Star, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface RoadmapFeature {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  release: string;
  timeline: string;
  tier: 'premium' | 'enterprise' | 'all';
  status: 'planning' | 'development' | 'beta' | 'released';
  path: string;
  keywords: string[];
}

const roadmapFeatures: RoadmapFeature[] = [
  {
    id: 'image-memories',
    title: 'Visual AI Memory Storage',
    description: 'Images coming soon, get early access.',
    icon: Camera,
    release: 'Coming soon',
    timeline: 'Early access list',
    tier: 'premium',
    status: 'planning',
    path: '/roadmap/image-memories',
    keywords: ['AI image memory', 'visual memory storage', 'semantic image search', 'OCR API', 'multimodal AI']
  },
  {
    id: 'audio-memories',
    title: 'AI Audio Memory & Speech Intelligence',
    description: 'Voice memories, transcribed and searchable, on paid plans. Starter includes 10 minutes per file, Pro 1 hour per file, and Enterprise 2 hours per file. Use the web app, REST API, or Python SDK.',
    icon: Mic,
    release: 'Paid plans',
    timeline: 'Available now',
    tier: 'premium',
    status: 'beta',
    path: '/roadmap/audio-memories',
    keywords: ['AI audio memory', 'speech to text API', 'voice memory storage', 'audio transcription', 'meeting transcription']
  },
  {
    id: 'video-memories',
    title: 'Enterprise Video AI Memory & Analysis',
    description: 'Video coming soon, get early access.',
    icon: Video,
    release: 'Coming soon',
    timeline: 'Interest list',
    tier: 'enterprise',
    status: 'planning',
    path: '/roadmap/video-memories',
    keywords: ['enterprise video AI', 'video memory storage', 'scene analysis', 'video transcription API', 'video intelligence']
  }
];

const getTierBadge = (tier: string) => {
  switch (tier) {
    case 'premium':
      return <Badge variant="secondary">Premium Feature</Badge>;
    case 'enterprise':
      return <Badge variant="secondary"><Star className="w-3 h-3 mr-1" />Enterprise</Badge>;
    default:
      return <Badge variant="secondary">All Plans</Badge>;
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'planning':
      return <Badge variant="outline">Planning</Badge>;
    case 'development':
      return <Badge variant="outline">In Development</Badge>;
    case 'beta':
      return <Badge variant="outline">Beta</Badge>;
    case 'released':
      return <Badge variant="secondary">Released</Badge>;
    default:
      return null;
  }
};

export default function RoadmapNavigation() {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* SEO-Optimized Header */}
      <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Memara Product Roadmap
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 text-3xl sm:text-4xl mt-2">
              Multimodal AI Memory Platform
            </span>
          </h1>
          
          <p className="mt-6 text-xl leading-8 text-gray-600 max-w-3xl mx-auto">
            Text memory is available today. Voice memories, transcribed and searchable, are on paid plans.{' '}
            <Link to="/roadmap/image-memories#image-interest" className="underline">
              Images coming soon, get early access
            </Link>
            .
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {roadmapFeatures.flatMap(f => f.keywords.slice(0, 2)).map((keyword, index) => (
              <Badge key={index} variant="outline">
                {keyword}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap Features Grid */}
      <section className="px-6 pb-16 mx-auto max-w-7xl lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {roadmapFeatures.map((feature) => {
            const Icon = feature.icon;
            const isCurrentPage = location.pathname === feature.path;
            
            return (
              <Card 
                key={feature.id} 
                className={`hover:shadow-lg transition-all duration-200 ${
                  isCurrentPage ? 'ring-2 ring-purple-500 shadow-lg' : 'hover:scale-105'
                }`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex flex-col gap-2">
                      {getTierBadge(feature.tier)}
                      {getStatusBadge(feature.status)}
                    </div>
                  </div>
                  
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <span>{feature.release} • {feature.timeline}</span>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <CardDescription className="text-base mb-6">
                    {feature.description}
                  </CardDescription>
                  
                  <div className="flex flex-wrap gap-1 mb-6">
                    {feature.keywords.slice(0, 3).map((keyword, index) => (
                      <Badge key={index} variant="outline">
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                  
                  <Link to={feature.path}>
                    <Button 
                      className={`w-full ${
                        isCurrentPage 
                          ? 'bg-purple-600 hover:bg-purple-700' 
                          : 'bg-gray-900 hover:bg-gray-800'
                      }`}
                    >
                      {isCurrentPage ? 'Currently Viewing' : 'Learn More'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Developer CTA Section */}
      <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8 bg-gray-50">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-6">
            Start Building with Memara Today
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            While these advanced features are in development, you can start building 
            intelligent AI applications with our powerful text memory capabilities.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-purple-600 hover:bg-purple-700">
              Start Building with Text Memories
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button size="lg" variant="outline">
              View API Documentation
            </Button>
          </div>
        </div>
      </section>

      {/* SEO Footer Information */}
      <section className="px-6 py-8 mx-auto max-w-7xl lg:px-8 border-t">
        <div className="text-center text-sm text-gray-500">
          <p className="mb-2">
            <strong>Memara AI Memory Platform Roadmap</strong> - Multimodal memory storage for AI developers
          </p>
          <p>
            Text and voice today.{' '}
            <Link to="/roadmap/image-memories#image-interest" className="underline">
              Images coming soon, get early access
            </Link>
            .{' '}
            <Link to="/roadmap/image-memories#image-interest" className="underline">
              Video coming soon, get early access
            </Link>
            .
          </p>
        </div>
      </section>
    </div>
  );
} 